export interface PricingTier {
  id: 'silver' | 'gold' | 'custom';
  name: string;
  description: string;
  pricePerParcel?: number; // Optional for custom
  // No setupFee field: setup is computed from MONTANA_CONSTANTS.SETUP_RATES_PER_PARCEL
  // (Silver $30/parcel, Gold $60/parcel) in pricingHelpers.getPricing, which is what
  // the page renders. A static per-tier figure here would drift from the contract.
  popular?: boolean;
  features: string[];
  notIncluded?: string[];
  ctaText: string;
  badge?: string;
}

export interface VideoDemo {
  id: string;
  title: string;
  description: string;
  userFlow: string;
  duration: string;
  thumbnail: string;
  videoUrl?: string;
}

export interface Testimonial {
  name: string;
  role: string;
  community: string;
  quote: string;
  avatar?: string;
  metrics?: {
    label: string;
    value: string;
  }[];
}

export interface CompetitorComparison {
  feature: string;
  silver: boolean | string;
  gold: boolean | string;
  custom: boolean | string;
  nextdoor: boolean | string;
  appfolio: boolean | string;
  buildium: boolean | string;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: 'pricing' | 'features' | 'implementation' | 'support';
}

export const MONTANA_PRICING_TIERS: PricingTier[] = [
  {
    id: 'silver',
    name: 'Silver Package',
    description: 'Transform community operations with digital governance and financial tools',
    pricePerParcel: 5,
    ctaText: 'Start with Silver',
    features: [
      // 🏛️ Democratic Decision Making
      'Secure voting that ends disputes and builds trust in decisions',
      'Proposal system where every resident voice matters',
      'Real-time voting results and participation tracking',

      // 💰 Community Financial Management
      'Accounts receivable and payable tracking',
      'Track community finances with transparent treasury tools',
      'Budget tracking and financial reporting',

      // 📱 Effortless Daily Living
      'Handle any community task in under 30 seconds on your phone',
      'Photo-based maintenance requests that actually get things fixed',
      'Emergency alerts that reach everyone instantly',
      'Direct messaging with management and neighbors',

      // 📊 Role-Specific Dashboards
      'Board member dashboard with governance and financial oversight',
      'Resident dashboard for payments, requests, and community engagement',

      // 🏗️ Infrastructure & Operations
      'Track every community asset and its maintenance history',
      "Meeting scheduling that works with everyone's calendar",
      'Digital document vault - find anything in seconds',

      // 🤝 Support & Training
      'Support response within 24 business hours',
      'Works seamlessly with your existing tools',
      'Responsive design works on desktop, laptop, tablet, and mobile devices',
    ],
  },
  {
    id: 'gold',
    name: 'Gold Package',
    description: 'Deeper governance, records, and financial tools with priority support',
    pricePerParcel: 8,
    popular: true,
    badge: 'Most Popular',
    ctaText: 'Choose Gold',
    features: [
      'Everything in Silver PLUS:',

      // 🏛️ Governance depth
      'Board internal email archive',
      'Attributed board voting with recorded rationale',
      'Governance workflows and dispute tracking',

      // 📚 Records depth
      'OCR of legacy paper archives up to a stated page volume',
      'AI meeting summaries',
      'Advanced tagging and cross-referencing',

      // 💰 Financial depth
      'Budget forecasting and multi-year planning',
      'Asset register with depreciation tracking',

      // 📊 Business Intelligence & Analytics
      'Custom reporting for board presentations',

      // 🔧 Enterprise Features
      'Custom branding that makes the platform yours',

      // 🏆 Premium Support
      'Priority support queue, response within 24 business hours',
      'Phone support for critical and high priority issues',
      'Quarterly review call',
      'On-site training available (billable at hourly rate)',
      'Commercially reasonable uptime with disaster recovery',
    ],
  },
];

export const MONTANA_VIDEO_DEMOS: VideoDemo[] = [
  {
    id: 'community-voting',
    title: 'Community Voting Made Simple',
    description: 'See how residents can easily submit proposals and vote on community issues',
    userFlow: 'New Proposal → Voting → Results',
    duration: '2:30',
    thumbnail: '/api/placeholder/400/225',
  },
  {
    id: 'treasury-transparency',
    title: 'Treasury Transparency in Action',
    description: 'Navigate the financial dashboard and track community investments',
    userFlow: 'Financial Dashboard → Transaction History → Reports',
    duration: '2:45',
    thumbnail: '/api/placeholder/400/225',
  },
  {
    id: 'asset-management',
    title: 'Managing Community Assets',
    description: 'Browse infrastructure inventory and track maintenance requests',
    userFlow: 'Asset Inventory → Maintenance Request → Work Order Tracking',
    duration: '3:00',
    thumbnail: '/api/placeholder/400/225',
  },
  {
    id: 'communication',
    title: 'Streamlined Communication',
    description: 'Emergency alerts, forums, and direct messaging in action',
    userFlow: 'Emergency Alert → Community Forum → Direct Messaging',
    duration: '2:15',
    thumbnail: '/api/placeholder/400/225',
  },
  {
    id: 'digital-transformation',
    title: 'From Paper to Digital',
    description: 'Document conversion and organization workflow demonstration',
    userFlow: 'Document Upload → Organization → Search & Access',
    duration: '2:50',
    thumbnail: '/api/placeholder/400/225',
  },
];

// PLACEHOLDER CONTENT. These are not real testimonials: no community has
// deployed the platform, so there are no customers to quote and no measured
// outcomes to report. The entries below describe what the product is intended
// to do, attributed to no one. Do not add invented names, invented quotations,
// or invented metrics here, and do not render this as social proof until real
// customers have agreed to be quoted.
export const MONTANA_TESTIMONIALS: Testimonial[] = [
  {
    name: 'Illustrative example',
    role: 'Board President',
    community: 'Sample Montana community',
    quote:
      'What a board should expect: proposals and votes handled online, so participation is not limited to who can attend a meeting in person.',
    metrics: [
      { label: 'Voting', value: 'From any device' },
      { label: 'Meeting Prep', value: 'Agenda and packet in one place' },
    ],
  },
  {
    name: 'Illustrative example',
    role: 'Community Manager',
    community: 'Sample rural Montana HOA',
    quote:
      'What a manager should expect: infrastructure and maintenance items tracked in one register instead of across email threads and filing cabinets.',
    metrics: [
      { label: 'Work Orders', value: 'Tracked to closure' },
      { label: 'History', value: 'Retained per asset' },
    ],
  },
  {
    name: 'Illustrative example',
    role: 'Treasurer',
    community: 'Sample Montana community',
    quote:
      'What a treasurer should expect: governing documents and financial records searchable in one place rather than scattered across paper archives.',
    metrics: [
      { label: 'Documents', value: 'Searchable' },
      { label: 'Access', value: 'Immediate' },
    ],
  },
];

export const MONTANA_FAQS: FAQ[] = [
  {
    id: 'pricing-calculation',
    question: 'How is pricing calculated for our community?',
    answer:
      'Pricing is simple and transparent: Package price × Number of voting parcels. For example, with 406 parcels on the Bronze plan: $10 × 406 = $4,060/month. There are no hidden fees or user limits.',
    category: 'pricing',
  },
  {
    id: 'setup-process',
    question: 'What does the setup process involve?',
    answer:
      'Our setup includes platform configuration, data migration, user account creation, training sessions, and ongoing support. Timeline is typically 4-6 weeks from contract signing to full deployment.',
    category: 'implementation',
  },
  {
    id: 'analog-digital',
    question: 'How does the analog-to-digital conversion work?',
    answer:
      'You scan your physical documents and upload them to our secure portal. We then organize, categorize, and integrate them into your digital document vault. Pricing starts at $75/hour based on organization level.',
    category: 'features',
  },
  {
    id: 'mobile-access',
    question: 'Can residents access this on their phones?',
    answer:
      'Yes. The platform is a responsive web app that works on desktop, laptop, tablet, and mobile. The best experience is on desktop or laptop for full feature access, but residents can use phones for quick tasks like checking announcements, viewing documents, or submitting maintenance requests. Installable-app behavior such as offline mode and push notifications is a design goal, not built yet.',
    category: 'features',
  },
  {
    id: 'data-security',
    question: 'How secure is our community data?',
    answer:
      'We use bank-level encryption, regular security audits, and comply with all data protection regulations. Your data is backed up daily with commercially reasonable service levels.',
    category: 'support',
  },
  {
    id: 'support-training',
    question: 'What kind of support and training do you provide?',
    answer:
      'All plans include comprehensive training, documentation, and ongoing support. Silver and Gold plans include enhanced support and regular check-ins to ensure success.',
    category: 'support',
  },
];

export const COMPETITOR_COMPARISON: CompetitorComparison[] = [
  {
    feature: 'Monthly Cost (406 parcels)',
    silver: '$4,060',
    gold: '$10,150',
    custom: 'Custom Quote',
    nextdoor: 'Free (ads) + $39/mo business',
    appfolio: '$4,000-8,000+',
    buildium: '$2,500-5,000+',
  },
  {
    feature: 'Electronic Voting',
    silver: true,
    gold: true,
    custom: true,
    nextdoor: 'Polls only',
    appfolio: false,
    buildium: false,
  },
  {
    feature: 'Treasury Management',
    silver: 'Basic',
    gold: 'Advanced',
    custom: 'Enterprise',
    nextdoor: false,
    appfolio: true,
    buildium: true,
  },
  {
    feature: 'Asset/Infrastructure Tracking',
    silver: 'Basic',
    gold: 'Advanced',
    custom: 'Enterprise',
    nextdoor: false,
    appfolio: 'Property only',
    buildium: 'Property only',
  },
  {
    feature: 'Emergency Alerts',
    silver: true,
    gold: true,
    custom: true,
    nextdoor: 'Posts only',
    appfolio: false,
    buildium: false,
  },
  {
    feature: 'Implementation Time',
    silver: '4-6 weeks',
    gold: '4-6 weeks',
    custom: '6-8 weeks',
    nextdoor: '1 day',
    appfolio: '12-20 weeks',
    buildium: '8-12 weeks',
  },
];
